import { useParams, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import Modal from 'fast-modal-library';

/**
 * AdminUser est une page qui affiche les détails d'un utilisateur spécifique.
 * Elle utilise le hook useParams de 'react-router-dom' pour obtenir l'ID de l'utilisateur à partir de l'URL.
 * Elle utilise ensuite l'ID pour faire une requête HTTP GET à l'API et obtenir les détails de l'utilisateur.
 * Elle affiche ensuite ces détails à l'administrateur.
 * Elle fournit également un formulaire pour mettre à jour les détails de l'utilisateur, qui fait une requête HTTP PUT à l'API.
 * Elle fournit également une fonction pour supprimer l'utilisateur, qui fait une requête HTTP DELETE à l'API.
 * Cette page utilise le hook useState de React pour gérer l'état de l'utilisateur.
 * Cette page utilise le hook useEffect de React pour appeler l'API lors du chargement de la page.
 * Cette page utilise le hook useSelector de 'react-redux' pour accéder au token de l'utilisateur dans le store Redux.
 * Cette page utilise le hook useNavigate de 'react-router-dom' pour naviguer vers une autre page après la suppression de l'utilisateur.
 * 
 */
const forbiddenEmailPatterns = ["intradef.gouv.fr"];
const FORBIDDEN_EMAIL_MESSAGE = "Cette adresse e-mail n'est pas autorisée sur cette plateforme.";

const containsForbiddenPattern = (email, appNumber) => {
    if (Number(appNumber) !== 5) return false;
    const lowerEmail = String(email || "").toLowerCase();
    return forbiddenEmailPatterns.some((pattern) =>
        lowerEmail.includes(pattern.toLowerCase())
    );
};

const apiPrefix = process.env.REACT_APP_API_PREFIX;

const UserDetail = () => {
    const Numberapp = useSelector((state) => state.app.appNumber);
    const { id } = useParams(); 
    const [user, setUser] = useState(null); 
    const [showModal, setShowModal] = useState(false);
    const [message, setMessage] = useState('');
    const [formError, setFormError] = useState('');
    const navigate = useNavigate(); 
    const token = useSelector((state) => state.user.token);

    const hide = () => {
        setShowModal(false);
        navigate('/admin/users'); 
    };

    useEffect(() => {
        const fetchUserDetails = async () => {
          try {
            const response = await fetch(apiPrefix + `users/${id}`, {
              method: 'GET',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
                'X-App-Number': Numberapp // Ajout de l'en-tête personnalisé pour l'application
              },
            });
      
            if (!response.ok) {
              throw new Error('Erreur lors de la récupération des détails de l\'utilisateur');
            }
      
            const data = await response.json();
            setUser(data); // Mettre à jour l'état de l'utilisateur avec les données récupérées
          } catch (error) {
            console.error('Erreur lors de la récupération des détails de l\'utilisateur', error);
          }
        };
      
        fetchUserDetails();
      }, [id, apiPrefix, Numberapp]); // Ajout de Numberapp aux dépendances
      

    const handleDelete = () => {
        if (window.confirm('Êtes-vous sûr de vouloir supprimer cet utilisateur ?')) {
            // Supprimer l'utilisateur
            // URL DEV
            //fetch(`http://localhost:3001/users/${id}`, {
            // URL PROD
            fetch(apiPrefix +`users/${id}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${token}`, 
                    'X-App-Number': Numberapp 
                },

            })
            .then(response => {
                if (!response.ok) {
                    throw new Error('Erreur lors de la suppression de l\'utilisateur');
                }
                navigate('/'); 
            })
            .catch(error => {
                console.error('Erreur lors de la suppression de l\'utilisateur', error);
            });
        }
    };

    const handleSubmit = (event) => {
        event.preventDefault();
        if (containsForbiddenPattern(user.username, Numberapp)) {
            setFormError(FORBIDDEN_EMAIL_MESSAGE);
            return;
        }
        setFormError('');
        fetch(apiPrefix +`admin/update/${id}`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`, 
            },
            body: JSON.stringify({
                ...user,
                appNumber: Numberapp

        }),
        })
        .then(response => response.json())
        .then(data => {
            setMessage('Profil mis à jour avec succès');
            setShowModal(true);
        })
        .catch(error => {
            console.error('Erreur lors de la mise à jour du profil', error);
        });
    };

    if (!user) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-theme-primary mx-auto mb-4"></div>
                    <p className="text-theme-primary text-lg">Chargement...</p>
                </div>
            </div>
        ); 
    }

    const handleChange = (event) => {
        const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
        setUser({ ...user, [event.target.name]: value });
        if (event.target.name === 'username') {
            setFormError(
                containsForbiddenPattern(value, Numberapp) ? FORBIDDEN_EMAIL_MESSAGE : ''
            );
        }
    };
    
    return (
        <div className="min-h-screen bg-gray-50 py-8 px-4">
            <div className="max-w-4xl mx-auto">
                <div className="mb-6">
                    <Link 
                        to="/admin/users" 
                        className="inline-flex items-center text-theme-primary hover:text-theme-accent transition-colors duration-200 mb-4"
                    >
                        <i className="fas fa-arrow-left mr-2"></i>
                        Retour à la page des utilisateurs
                    </Link>
                </div>

                <div className="card-theme mb-8">
                    <div className="flex items-center justify-between mb-6">
                        <h1 className="text-3xl font-bold text-theme-primary">{user.username}</h1>
                        <div className="flex space-x-2">
                            <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                                user.active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                            }`}>
                                {user.active ? 'Actif' : 'Inactif'}
                            </span>
                            {user.is_admin && (
                                <span className="px-3 py-1 rounded-full text-sm font-semibold bg-blue-100 text-blue-800">
                                    Admin
                                </span>
                            )}
                            {user.chefEntreprise && (
                                <span className="px-3 py-1 rounded-full text-sm font-semibold bg-purple-100 text-purple-800">
                                    Chef d'entreprise
                                </span>
                            )}
                        </div>
                    </div>
                    
                    <div className="bg-gray-50 p-4 rounded-lg mb-6">
                        <div className="flex items-center">
                            <i className="fas fa-clock text-theme-accent mr-2"></i>
                            <span className="text-gray-700">
                                {user.lastactive === null ? 'Pas d\'activité' : 
                                    `Dernière activité: ${new Date(user.lastactive).toLocaleString()}`}
                            </span>
                        </div>
                    </div>
                </div>

                <form onSubmit={handleSubmit} className="card-theme">
                    <h2 className="text-2xl font-bold text-theme-primary mb-6">Modifier les informations</h2>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                <i className="fas fa-user mr-2 text-theme-accent"></i>
                                Nom d'utilisateur
                            </label>
                            <input 
                                type="text" 
                                name="username" 
                                value={user.username} 
                                onChange={handleChange}
                                className="input-theme"
                            />
                            {formError && (
                                <p className="mt-2 text-sm text-red-600">{formError}</p>
                            )}
                        </div>
                        
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                <i className="fas fa-user-tag mr-2 text-theme-accent"></i>
                                Prénom
                            </label>
                            <input 
                                type="text" 
                                name="firstname" 
                                value={user.firstname} 
                                onChange={handleChange}
                                className="input-theme"
                            />
                        </div>
                        
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                <i className="fas fa-user mr-2 text-theme-accent"></i>
                                Nom
                            </label>
                            <input 
                                type="text" 
                                name="lastname" 
                                value={user.lastname} 
                                onChange={handleChange}
                                className="input-theme"
                            />
                        </div>
                        
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                <i className="fas fa-building mr-2 text-theme-accent"></i>
                                Société
                            </label>
                            <input 
                                type="text" 
                                name="company" 
                                value={user.company} 
                                onChange={handleChange}
                                className="input-theme"
                            />
                        </div>
                        
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                <i className="fas fa-id-card mr-2 text-theme-accent"></i>
                                Siret
                            </label>
                            <input 
                                type="text" 
                                name="siret" 
                                value={user.siret} 
                                onChange={handleChange}
                                className="input-theme"
                            />
                        </div>
                    </div>

                    <div className="border-t border-gray-200 pt-6 mb-6">
                        <h3 className="text-lg font-semibold text-theme-primary mb-4">Permissions</h3>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <label className="flex items-center p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer">
                                <input 
                                    type="checkbox" 
                                    name="active" 
                                    checked={user.active} 
                                    onChange={handleChange}
                                    className="rounded border-gray-300 text-theme-accent focus:ring-theme-accent mr-3"
                                />
                                <div>
                                    <div className="font-medium text-gray-900">Actif</div>
                                    <div className="text-sm text-gray-500">Utilisateur actif</div>
                                </div>
                            </label>
                            
                            <label className="flex items-center p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer">
                                <input 
                                    type="checkbox" 
                                    name="is_admin" 
                                    checked={user.is_admin} 
                                    onChange={handleChange}
                                    className="rounded border-gray-300 text-theme-accent focus:ring-theme-accent mr-3"
                                />
                                <div>
                                    <div className="font-medium text-gray-900">Administrateur</div>
                                    <div className="text-sm text-gray-500">Accès admin</div>
                                </div>
                            </label>
                            
                            <label className="flex items-center p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer">
                                <input 
                                    type="checkbox" 
                                    name="chefEntreprise" 
                                    checked={user.chefEntreprise} 
                                    onChange={handleChange}
                                    className="rounded border-gray-300 text-theme-accent focus:ring-theme-accent mr-3"
                                />
                                <div>
                                    <div className="font-medium text-gray-900">Chef d'entreprise</div>
                                    <div className="text-sm text-gray-500">Gestion entreprise</div>
                                </div>
                            </label>
                        </div>
                    </div>

                    <div className="flex flex-col sm:flex-row gap-4">
                        <button 
                            type="submit" 
                            className="btn-theme flex-1"
                        >
                            <i className="fas fa-save mr-2"></i>
                            Mettre à jour
                        </button>
                        <button 
                            type="button" 
                            onClick={handleDelete}
                            className="btn-theme bg-red-600 hover:bg-red-700 flex-1"
                        >
                            <i className="fas fa-trash mr-2"></i>
                            Supprimer
                        </button>
                    </div>
                    
                    {showModal && (
                        <Modal contentModal={message} hide={hide} />
                    )}
                </form>
            </div>
        </div>
    );
};

export default UserDetail;
